import React from 'react';
import PropTypes from 'prop-types';

import AvailableLanguageLink from '../AvailableLanguageLink';
import { Link } from 'src/components/Inputs';

/*
 * When making changes to this file be sure that any changes
 * also are made to the corresponding iOS specific counterpart
 * in src/pages/<lang>/legal/ios.jsx.
 */

const propTypes = {
  id: PropTypes.string,
  availableLanguages: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
};

const EsVersion = ({ id, availableLanguages }) => {
  return (
    <>
      <h4 id={id || 'política-de-cookies'}>Política de cookies</h4>

      <p>Available languages:</p>
      <ul>
        {availableLanguages.map((language, i) => {
          return (
            <AvailableLanguageLink
              key={`language-${i}`}
              href={language.href}
              label={language.label}
            />
          );
        })}
      </ul>
      <br />
      <br />

      <p>Fecha de entrada en vigor: 22 de agosto de 2023</p>
      <p>
        Esta política de cookies (la “Política”) explica cómo Plaid Inc. y sus
        filiales, incluida Plaid Financial Ltd., Plaid Consumer Reporting
        Agency, Inc., y Plaid, B.V., (en su conjunto, “nosotros”, “nuestro” o
        “nos”) y los terceros de confianza utilizan cookies en nuestros sitios
        web y otros servicios en línea (en su conjunto, los “Servicios”), así
        como las opciones que usted tiene con relación a dichas cookies. Le
        recomendamos que lea esta Política para obtener más información sobre
        los tipos de cookies que utilizamos, por qué las usamos y cómo puede
        controlar o limitar su uso.
      </p>
      <p>
        Para conocer mejor nuestras prácticas de privacidad, consulte nuestra{' '}
        <Link href='#política-de-privacidad-aplicable-al-usuario-final'>
          <a>Política de privacidad aplicable al usuario final</a>
        </Link>{' '}
        y nuestra{' '}
        <Link href='#declaración-de-privacidad'>
          <a>Declaración de privacidad</a>
        </Link>
        .
      </p>

      <p>
        <em>Ir a la sección:</em>
      </p>
      <p>
        <Link href='#qué-son-las-cookies'>
          <a data-smooth-scroll data-offset='80'>
            ¿Qué son las cookies?
          </a>
        </Link>
      </p>

      <p>
        <Link href='#cómo-usamos-las-cookies'>
          <a data-smooth-scroll data-offset='80'>
            Cómo usamos las cookies
          </a>
        </Link>
      </p>

      <p>
        <Link href='#sus-opciones-cookies'>
          <a data-smooth-scroll data-offset='80'>
            Sus opciones
          </a>
        </Link>
      </p>

      <p>
        <Link href='#cambios-en-esta-política-cookies'>
          <a data-smooth-scroll data-offset='80'>
            Cambios en esta política
          </a>
          '
        </Link>
      </p>

      <p>
        <Link href='#contacto-cookies'>
          <a data-smooth-scroll data-offset='80'>
            Contacto
          </a>
        </Link>
      </p>

      <h5 id='qué-son-las-cookies'>¿Qué son las cookies?</h5>
      <p>
        Las cookies son pequeños archivos de datos que se almacenan en su
        navegador o dispositivo y que pueden adoptar la forma de cookies de
        sesión (caducan cuando cierra el navegador web) o cookies persistentes
        (permanecen en su navegador durante el plazo predeterminado o hasta que
        las elimine). Suelen contener información relativa a las preferencias
        del sitio web, los ajustes del usuario y el historial de navegación.
        Esta información nos ayuda a reconocerle cuando interactúa o vuelve a
        visitar nuestros Servicios.
      </p>
      <p>
        Las cookies pueden proceder de la entidad que administra el sitio web
        que está visitando (“cookies propias”) o de otras empresas (“cookies de
        terceros”). Por ejemplo, usamos cookies propias para recordar sus
        ajustes y preferencias, como el idioma y la ubicación. También incluimos
        en nuestros sitios web cookies analíticas de terceros, como Google
        Analytics, que nos ayudan a entender cómo utiliza nuestros sitios web,
        con la finalidad de mejorarlos.
      </p>
      <p>
        También usamos otras tecnologías que funcionan de manera similar a las
        cookies. Por ejemplo, usamos balizas web (también denominadas píxeles),
        que son pequeñas imágenes en la página web o en un correo electrónico.
        Las balizas web recopilan información de su navegador o dispositivo y
        pueden configurar cookies. Además, utilizamos el almacenamiento local,
        que permite almacenar datos de manera local en su navegador o
        dispositivo.
      </p>

      <h5 id='cómo-usamos-las-cookies'>Cómo usamos las cookies</h5>
      <p>
        Utilizamos cookies por una serie de razones, como ayudarnos a conocer la
        popularidad de determinado contenido, mejorar nuestros Servicios y su
        experiencia, y entender mejor su actividad en línea. Las cookies que
        solemos usar responden a una o más de las siguientes categorías:
      </p>
      <div className='eupp-container'>
        <table>
          <thead>
            <tr>
              <th>Categorías de cookies</th>
              <th>Cómo usamos estas cookies</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Necesarias</td>
              <td>
                Estas cookies son estrictamente necesarias para que nuestros
                Servicios funcionen correctamente. Algunos de nuestros Servicios
                dependen de estas cookies, de manera que no funcionarán
                correctamente (o ni siquiera funcionarán) si están
                deshabilitadas.
              </td>
            </tr>
            <tr>
              <td>Funcionales</td>
              <td>
                Estas cookies permiten que el sitio web pueda ofrecer
                personalización y funcionalidades mejoradas. Pueden ser
                establecidas por nosotros o por proveedores externos cuyos
                servicios hemos incorporado a nuestras páginas. Si usted no
                permite estas cookies, es posible que algunos o ninguno de estos
                servicios funcionen correctamente.
              </td>
            </tr>
            <tr>
              <td>Rendimiento</td>
              <td>
                Usamos estas cookies para comprender mejor cómo interactúa con
                nuestros Servicios para poder mejorarlos.
                <br />
                <br />
                Por ejemplo, podemos utilizar estas cookies para determinar si
                ha interactuado con determinados contenidos o funciones. También
                podemos utilizar estas cookies para obtener más información
                sobre qué funciones son las más populares entre nuestros
                usuarios y dónde podemos necesitar realizar mejoras.
              </td>
            </tr>
            <tr>
              <td>Segmentación/Publicidad</td>
              <td>
                Nosotros y nuestros socios publicitarios de confianza utilizamos
                estas cookies para mostrar anuncios, hacer que los anuncios sean
                más relevantes para los visitantes de nuestros sitios web y para
                rastrear la eficiencia de cualquier campaña publicitaria, tanto
                en nuestros Servicios como en otros sitios web.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h5 id='sus-opciones-cookies'>Sus opciones</h5>
      <p>
        Tiene derecho a decidir si acepta o no estas cookies. Sin embargo,
        puesto que el funcionamiento de nuestros Servicios depende en gran
        medida de ellas, eliminarlas o rechazarlas podría afectar la
        disponibilidad y funcionalidad de nuestros Servicios.
      </p>
      <p>
        Le indicamos a continuación algunas opciones para controlar o limitar el
        uso de estas cookies en nuestros Servicios:
      </p>
      <ul>
        <li>
          <p>
            Podrá utilizar normalmente los ajustes para eliminar o rechazar
            manualmente las cookies en los ajustes de configuración del
            navegador. Para configurar estos ajustes, siga las instrucciones de
            su navegador.
          </p>
        </li>
        <li>
          <p>
            Para evitar que Google Analytics utilice sus datos, podrá instalar
            el{' '}
            <Link href='https://tools.google.com/dlpage/gaoptout'>
              <a>Complemento de inhabilitación para navegadores de Google.</a>
            </Link>
            .
          </p>
        </li>
        <li>
          <p>
            Para obtener información sobre cómo nuestros socios anunciantes
            pueden permitirle inhabilitar la recepción de anuncios basados en su
            historial de navegación, visite{' '}
            <Link href='http://optout.aboutads.info/'>
              <a>http://optout.aboutads.info/</a>
            </Link>
            . Además, aconsejamos a los usuarios europeos visitar la{' '}
            <Link href='http://www.youronlinechoices.eu/'>
              <a>Alianza Europea de Publicidad Digital Interactiva</a>
            </Link>{' '}
            y a los usuarios canadienses, la{' '}
            <Link href='https://youradchoices.ca/'>
              <a>Alianza Canadiense de Publicidad Digital</a>
            </Link>{' '}
            , para obtener más información sobre cómo inhabilitar la recepción
            de este tipo de anuncios.
          </p>
        </li>
      </ul>

      <h5 id='cambios-en-esta-política-cookies'>Cambios en esta política</h5>
      <p>
        Podemos actualizar o modificar ocasionalmente las cookies (o tecnologías
        similares) que utilizamos. Publicaremos la última versión de esta
        Política en nuestro sitio web en{' '}
        <Link href='https://plaid.com/legal'>
          <a>https://plaid.com/legal</a>
        </Link>
        . Si efectuamos cualquier cambio, actualizaremos la fecha de entrada en
        vigor que figura en la parte superior de esta política. Le recomendamos
        que periódicamente consulte si se han introducido cambios.
      </p>

      <h5 id='contacto-cookies'>Contacto</h5>
      <p>
        Si tiene cualquier pregunta sobre el uso que hacemos de las cookies o
        sobre esta Política, póngase en contacto con nosotros en{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>
        .
      </p>

      <hr className='section-divider' />
    </>
  );
};

EsVersion.propTypes = propTypes;
export default EsVersion;
